import { Component, OnInit, HostBinding, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SnackbarConfig, DataSourceConfig, SnackbarType } from '@core/models';
import { tap, map, withLatestFrom, delay } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Select, Store } from '@ngxs/store';
import { UiState } from './ngxs-store/ui-store/ui.state';
import { EnableMobileModeAction, DisableMobileModeAction, CloseSnackbarAction, ToggleIsInferiorBrowserAction } from './ngxs-store/ui-store/ui.actions';
import { SwUpdate } from '@angular/service-worker';
import { UpdateDialogComponent } from '@core/components/update-dialog/update-dialog.component';
import { DataSourceConfigsState } from '@ngxs-store/data-source-configs-store/data-source-configs.state';
import { UpdateDataSourceConfigAction } from '@ngxs-store/data-source-configs-store/data-source-configs.actions';
import { OverlayContainer } from '@angular/cdk/overlay';
import { routeAnimations } from '@core/animations/route.animations';
import browser from 'browser-detect';
import { ProblemDetailSnackbarComponent } from '@shared/components/problem-detail-snackbar/problem-detail-snackbar.component';
import { SuperSnackbarComponent } from '@shared/components/super-snackbar/super-snackbar.component';

@Component({
  selector: 'app-root',
  animations: [routeAnimations],
  template: `
    <div [hidden]="(showSpinner$ | async) === false"><mat-progress-bar mode="indeterminate" color="accent"></mat-progress-bar></div>
    <app-deployment-banner></app-deployment-banner>
    <div class="animation-container" [@routeAnimations]="o.isActivated && o.activatedRoute.url">
      <router-outlet #o="outlet"></router-outlet>
    </div>
    <app-version-display></app-version-display>
  `,
  styles: [
    `
      .mat-progress-bar {
        position: fixed;
        top: 0px;
        z-index: 3;
      }

      .animation-container {
        height: 100%;
      }
    `
  ]
})
export class AppComponent implements OnInit {
  @Select(UiState.getTheme) theme$: Observable<string>;
  @Select(UiState.getSnackbarConfig) snackBarConfig$: Observable<SnackbarConfig>;
  @Select(DataSourceConfigsState.getDataSourceConfigs) dataSourceConfigs$: Observable<DataSourceConfig[]>;

  showSpinner$: Observable<boolean>;
  @HostBinding('class') componentCssClass: any;

  constructor(
    private store: Store,
    private zone: NgZone,
    private swUpdate: SwUpdate,
    public overlayContainer: OverlayContainer,
    public snackBar: MatSnackBar,
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver
  ) { }

  ngOnInit() {
    this.initInferiorBrowser();
    this.initThemeSubscription();
    this.initSnackbarSubscription();
    this.checkForUpdate();
    this.showSpinner$ = this.store.select(UiState.getShowSpinner).pipe(delay(0));
  }

  initInferiorBrowser() {
    this.store.dispatch(
      new ToggleIsInferiorBrowserAction({
        isInferiorBrowser: environment.inferiorBrowsers.includes(browser().name)
      })
    );
  }

  initThemeSubscription() {
    this.theme$.subscribe((theme: string) => {
      const themeClassesToRemove = Array.from(this.overlayContainer.getContainerElement().classList).filter((item: string) => item.includes('-theme'));

      if (themeClassesToRemove.length) {
        this.overlayContainer.getContainerElement().classList.remove(...themeClassesToRemove);
      }

      this.overlayContainer.getContainerElement().classList.add(theme);
      this.componentCssClass = theme;
    });
  }

  initSnackbarSubscription() {
    this.snackBarConfig$
      .pipe(
        tap((snackBarConfig) => {
          if (snackBarConfig && snackBarConfig.show) {

            switch (snackBarConfig.type) {
              case SnackbarType.Simple:

                const simpleSnackbarRef = this.snackBar.open(snackBarConfig.message, 'CLOSE', { duration: environment.globalHttpErrorSnackbarDuration });
                simpleSnackbarRef.afterDismissed().subscribe(() => {
                  this.store.dispatch(new CloseSnackbarAction());
                });

                break;

              case SnackbarType.Super:

                this.zone.run(() => {
                  const advancedSnackbarRef = this.snackBar.openFromComponent(SuperSnackbarComponent, {
                    data: { message: snackBarConfig.message },
                    panelClass: 'snackbar-container'
                  });

                  advancedSnackbarRef.afterDismissed().subscribe(() => {
                    this.store.dispatch(new CloseSnackbarAction());
                  });
                });

                break;

              case SnackbarType.ProblemDetail:

                this.zone.run(() => {
                  const problemDetailSnackBarRef = this.snackBar.openFromComponent(ProblemDetailSnackbarComponent, {
                    data: { problemDetail: snackBarConfig.problemDetail },
                    panelClass: 'snackbar-container'
                  });

                  problemDetailSnackBarRef.afterDismissed().subscribe(() => {
                    this.store.dispatch(new CloseSnackbarAction());
                  });
                });

                break;
            }
          }
        })
      )
      .subscribe();
  }


  checkForUpdate() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.available.subscribe(() => {
        const dialogRef = this.dialog.open(UpdateDialogComponent, {
          autoFocus: false
        });

        dialogRef.afterClosed().subscribe((res) => {
          if (res) {
            this.swUpdate.activateUpdate().then(() => window.location.reload());
          }
        });
      });
    }
  }
}
